
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const MODAL_BREAKPOINT = 990;

/**
 * Horizon - Theme Drawer
 * Bolton Couture: proteccion contra cierres accidentales.
 *
 * Conserva las funciones originales de Horizon:
 * apertura, cierre, animaciones, foco, scroll,
 * dialogos anidados y adaptacion movil.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  static #stackOrder = 0;

  #isClosing = false;
  #deferredOpen = false;
  #previouslyFocused = null;

  #modalQuery = window.matchMedia(
    `(max-width: ${MODAL_BREAKPOINT - 1}px)`
  );

  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#modalQuery.addEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    const restoredOrder = Number(
      this.style.getPropertyValue('--drawer-stack-order') || 0
    );

    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  #onRestore() {
    const { panel } = this.refs;

    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    unlockScroll(this.refs.panel);

    this.#modalQuery.removeEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    this.#removeEventListeners();
    removeTrapFocus();

    this.#deferredOpen = false;
  }

  /**
   * BOLTON COUTURE
   * Proteccion frente a clics internos.
   *
   * Si el clic se origina dentro del panel, no cerrarlo,
   * aunque un componente elimine el elemento pulsado
   * durante el procesamiento del evento.
   *
   * Los clics reales sobre el fondo siguen cerrando
   * el drawer normalmente.
   *
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    const eventPath = event.composedPath();
    const panelIndex = eventPath.indexOf(panel);

    if (panelIndex > 0) {
      return;
    }

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) {
        return;
      }

      this.close();
    }
  };

  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  #getOpenNestedDialog() {
    return this.refs.panel.querySelector(
      'dialog[open]'
    );
  }

  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;

    const nestedDialog =
      this.#getOpenNestedDialog();

    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) &&
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(
        nestedDialog,
        nestedActiveElement
      );
    } else {
      this.#focusInitialElement();
    }

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    if (
      event.isComposing ||
      event.defaultPrevented
    ) {
      return;
    }

    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Abre el panel lateral.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }

    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    this.#previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    onAnimationEnd(
      panel,
      () => panel.classList.remove(openClass),
      { subtree: false }
    );

    this.setAttribute('open', '');

    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    onAnimationEnd(
      panel,
      () => panel.classList.remove(openClass),
      { subtree: false }
    );

    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.removeEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  #focusInitialElement() {
    const closeButton =
      this.refs.panel.querySelector(
        '.theme-drawer__close-button'
      );

    closeButton?.focus();
  }

  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (
      focusTarget &&
      document.contains(focusTarget) &&
      dialog.contains(focusTarget)
    ) {
      focusTarget.focus();
    }
  }

  /**
   * Cierra el panel con su animacion.
   * Conserva bloqueo de scroll y restauracion del foco.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;

    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    const closingAsModal =
      this.#modalQuery.matches;

    const scrollTopWhileLocked =
      closingAsModal ? getScrollTop() : null;

    this.removeAttribute('open');

    this.dispatchEvent(new DrawerCloseEvent());

    unlockScroll(panel);

    if (panel.open) {
      panel.classList.remove(
        'theme-drawer__dialog--opening',
        'theme-drawer__dialog--opening-inline-start'
      );

      panel.classList.add(
        'theme-drawer__dialog--closing'
      );

      await onAnimationEnd(
        panel,
        undefined,
        { subtree: false }
      );

      panel.classList.remove(
        'theme-drawer__dialog--closing'
      );
    }

    panel.close();

    this.style.removeProperty(
      '--drawer-stack-order'
    );

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;

    if (trigger && document.contains(trigger)) {
      trigger.focus({
        preventScroll: closingAsModal
      });
    } else {
      document
        .querySelector(
          `[aria-controls="${this.id}"]`
        )
        ?.focus({
          preventScroll: closingAsModal
        });
    }

    if (
      scrollTopWhileLocked != null &&
      getScrollTop() !== scrollTopWhileLocked
    ) {
      scrollTo({
        top: scrollTopWhileLocked,
        behavior: 'instant'
      });
    }

    this.#isClosing = false;

    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;

      this.open(options);
    }
  }

  async close() {
    if (this.#isClosing) {
      this.#deferredOpen = false;
      return;
    }

    await this.#closeDialog();
  }

  toggle() {
    if (
      this.isOpen &&
      this.#isTopmost()
    ) {
      return this.close();
    }

    return this.open();
  }

  #isTopmost() {
    const myOrder = Number(
      this.style.getPropertyValue(
        '--drawer-stack-order'
      ) || 0
    );

    for (
      const other of document.querySelectorAll(
        'theme-drawer[open]'
      )
    ) {
      if (other === this) continue;

      const otherOrder = Number(
        other.style.getPropertyValue(
          '--drawer-stack-order'
        ) || 0
      );

      if (otherOrder > myOrder) {
        return false;
      }
    }

    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define(
    'theme-drawer',
    ThemeDrawer
  );
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, {
      bubbles: true
    });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, {
      bubbles: true
    });
  }

  static eventName = 'theme-drawer:close';
}
